import React from "react";
import styles from "./PaginationControl.module.css";

type PaginationControlProps = {
    totalPages: number;
    currentPage: number;
    onPageChange: (page: number) => void;
};

const PaginationControl: React.FC<PaginationControlProps> = ({
    totalPages,
    currentPage,
    onPageChange,
}) => {
    const handlePageChange = (page: number) => {
        if (page >= 1 && page <= totalPages) {
            onPageChange(page);
        }
    };

    const renderPageNumbers = () => {
        const pageNumbers = [];

        // Always show the first page
        pageNumbers.push(
            <button
                key={1}
                className={`${styles.pageButton} ${currentPage === 1 ? styles.active : ""}`}
                onClick={() => handlePageChange(1)}
            >
                1
            </button>,
        );

        // Add ellipsis if the current page is greater than 2
        // (i.e., if there's a gap between 1 and currentPage)
        if (currentPage > 2) {
            pageNumbers.push(
                <span key="ellipsis-start" className={styles.ellipsis}>
                    ...
                </span>,
            );
        }

        // Show the current page (if it's not the first or last page)
        if (currentPage > 1 && currentPage < totalPages) {
            pageNumbers.push(
                <button
                    key={currentPage}
                    className={`${styles.pageButton} ${styles.active}`}
                    onClick={() => handlePageChange(currentPage)}
                >
                    {currentPage}
                </button>,
            );
        }

        // Add ellipsis if the current page is not close to the last page
        if (currentPage < totalPages - 1) {
            pageNumbers.push(
                <span key="ellipsis-end" className={styles.ellipsis}>
                    ...
                </span>,
            );
        }

        // Always show the last page
        if (totalPages > 1) {
            pageNumbers.push(
                <button
                    key={totalPages}
                    className={`${styles.pageButton} ${currentPage === totalPages ? styles.active : ""}`}
                    onClick={() => handlePageChange(totalPages)}
                >
                    {totalPages}
                </button>,
            );
        }

        return pageNumbers;
    };

    return (
        <div className={styles.paginationContainer}>
            <button
                className={styles.navButton}
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
            >
                Previous
            </button>
            {renderPageNumbers()}
            <button
                className={styles.navButton}
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
            >
                Next
            </button>
        </div>
    );
};

export default PaginationControl;
